import { useEffect, useState } from 'react';
import { Card, Row, Col, Statistic, DatePicker, Select, Segmented, Button, message } from 'antd';
import {
  PlayCircleOutlined,
  DesktopOutlined,
  VideoCameraOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import dayjs from 'dayjs';
import { reportAPI, screenAPI } from '../services/api';
import { themeColors } from '../config/theme';

const { RangePicker } = DatePicker;

const GROUP_BY_OPTIONS = [
  { label: 'Day', value: 'day' },
  { label: 'Week', value: 'week' },
  { label: 'Month', value: 'month' },
];

const periodFormat = (groupBy) => (groupBy === 'month' ? 'MMM YYYY' : 'MMM D');

export default function PlaybackReport() {
  const [range, setRange] = useState([dayjs().subtract(7, 'day'), dayjs()]);
  const [groupBy, setGroupBy] = useState('day');
  const [screens, setScreens] = useState([]);
  const [screenCode, setScreenCode] = useState(null);
  const [stats, setStats] = useState(null);
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchScreens();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchScreens = async () => {
    try {
      const response = await screenAPI.list({ limit: 100 });
      setScreens(response.data.data.data || []);
    } catch (error) {
      message.error('Failed to load screens');
    }
  };

  const fetchReport = async () => {
    if (!range || range.length !== 2) return;

    setLoading(true);
    try {
      const startDate = range[0].startOf('day').toISOString();
      const endDate = range[1].endOf('day').toISOString();

      const [statsRes, seriesRes] = await Promise.all([
        reportAPI.playback({ startDate, endDate, screenCode: screenCode || undefined }),
        reportAPI.playbackTimeseries({ startDate, endDate, groupBy, screenCode: screenCode || undefined }),
      ]);

      setStats(statsRes.data.data);
      setSeries(
        (seriesRes.data.data || []).map((row) => ({
          period: dayjs(row.period).format(periodFormat(groupBy)),
          plays: row.playback_count || 0,
        }))
      );
    } catch (error) {
      message.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupBy, screenCode]);

  const cardStyle = {
    backgroundColor: themeColors.bgElevated,
    borderColor: themeColors.border,
  };

  const statCards = [
    {
      title: 'Total Playbacks',
      value: stats?.total_playbacks || 0,
      icon: <PlayCircleOutlined style={{ color: themeColors.primary }} />,
      color: themeColors.primary,
    },
    {
      title: 'Screens Reached',
      value: stats?.unique_screens || 0,
      icon: <DesktopOutlined style={{ color: themeColors.info }} />,
      color: themeColors.info,
    },
    {
      title: 'Videos Played',
      value: stats?.unique_videos || 0,
      icon: <VideoCameraOutlined style={{ color: themeColors.success }} />,
      color: themeColors.success,
    },
    {
      title: 'Total Duration (s)',
      value: stats?.total_duration || 0,
      icon: <FieldTimeOutlined style={{ color: themeColors.warning }} />,
      color: themeColors.warning,
    },
  ];

  return (
    <div>
      <h2 style={{ color: themeColors.text, marginBottom: '24px' }}>Playback Report</h2>

      <Card style={{ ...cardStyle, marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <RangePicker value={range} onChange={(value) => setRange(value)} />
          <Select
            placeholder="Overall"
            allowClear
            style={{ width: 240 }}
            value={screenCode}
            onChange={setScreenCode}
            options={screens.map((s) => ({
              value: s.screen_code,
              label: `${s.screen_name} (${s.screen_code})`,
            }))}
          />
          <Segmented options={GROUP_BY_OPTIONS} value={groupBy} onChange={setGroupBy} />
          <Button type="primary" onClick={fetchReport} loading={loading}>
            Apply
          </Button>
        </div>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
        {statCards.map((card) => (
          <Col xs={24} sm={12} md={6} key={card.title}>
            <Card
              style={{ ...cardStyle, borderLeft: `3px solid ${card.color}` }}
              loading={loading}
            >
              <Statistic title={card.title} value={card.value} prefix={card.icon} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={cardStyle} loading={loading} title="Playback Trend">
        <ResponsiveContainer width="100%" height={340}>
          <LineChart data={series} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={themeColors.border} />
            <XAxis dataKey="period" stroke={themeColors.textSecondary} tick={{ fontSize: 12 }} />
            <YAxis stroke={themeColors.textSecondary} tick={{ fontSize: 12 }} allowDecimals={false} />
            <Tooltip
              contentStyle={{ backgroundColor: themeColors.bgElevated, borderColor: themeColors.border }}
              labelStyle={{ color: themeColors.text }}
            />
            <Line
              type="monotone"
              dataKey="plays"
              name="Playbacks"
              stroke={themeColors.primary}
              strokeWidth={2}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Card>
    </div>
  );
}
